import { NavLink, useLoaderData, useResolvedPath } from "react-router-dom";
import { Suspense, useState, useEffect } from "react";
import { defer, Await } from "react-router-dom";
import MinifigurePlaceholder from "../components/MinifigurePlaceholder";

/*
  MinifigureListSkeleton - Displayed while API data is loading.

  Shows 20 animated placeholder rows that match the layout of the real data.
  Uses Tailwind's animate-pulse for a subtle loading animation.
  This provides immediate visual feedback when navigating to this page.
*/
const MinifigureListSkeleton = () => {
    const skeletonRows = Array.from({ length: 20 }, (_, i) => i + 1);

    return (
        <>
            <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-50 p-3">
                <p className="grow basis-0 text-center font-bold hidden md:block">24h</p>
                <p className="grow basis-0 text-center font-bold hidden md:block">Position</p>
                <p className="grow basis-0 text-center font-bold block md:hidden">#</p>
                <p className="grow basis-0 text-center"></p>
                <p className="grow-[4] basis-0 font-bold">Name</p>
                <p className="grow basis-0 text-center font-bold">Rating</p>
                <p className="grow basis-0"></p>
            </div>
            {skeletonRows.map((position) => (
                <div key={position} className="flex gap-2 md:gap-10 border-b border-white border-opacity-20 p-3 animate-pulse">
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="w-6 h-6 bg-gray-700 rounded"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <p className="text-gray-500">{position}</p>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <MinifigurePlaceholder />
                    </div>
                    <div className="grow-[4] basis-0 flex items-center">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="h-4 bg-gray-700 rounded w-12"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="bg-gray-700 p-2 rounded-md w-20 h-10"></div>
                    </div>
                </div>
            ))}
        </>
    );
};

/*
  MinifigureRow - Renders a single row with progressive image loading.

  Each row tracks its own imageLoaded state independently, so images
  swap from placeholder to real image one-by-one as they finish loading,
  rather than waiting for all images to load.
*/
const MinifigureRow = ({ minifigure, url, minifigureImagePath }) => {
    // Tracks whether this row's image has finished loading
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
        <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-20 p-3">
            <div className="grow basis-0 flex justify-center items-center gap-1">
                {minifigure.currentPosition < minifigure.previousPosition ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="#4ade80"
                        className="w-6 h-6"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
                    </svg>
                ) : (
                    minifigure.previousPosition < minifigure.currentPosition && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="#f87171"
                            className="w-6 h-6"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                    )
                )}
                {
                    minifigure.currentPosition - minifigure.previousPosition > 0 ? (
                        <p className="text-red-400">{Math.abs(minifigure.previousPosition - minifigure.currentPosition)}</p>
                    ) : minifigure.currentPosition - minifigure.previousPosition < 0 ? (
                        <p className="text-green-400">{Math.abs(minifigure.currentPosition - minifigure.previousPosition)}</p>
                    ) : (
                        <p></p>
                    )
                }
            </div>
            <div className="grow basis-0 flex justify-center items-center">
                <p className="">
                    {minifigure.position === 1 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#3EEB3B"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#86FF8B"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#EED15C"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#D9BB44"/>
                        </svg>
                    : minifigure.position === 2 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#3EEB3B"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#86FF8B"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#D9D9D9"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#B9B7B7"/>
                        </svg>
                    : minifigure.position === 3 ?
                        <svg height="30" viewBox="0 0 441 430" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M295 200.5H147L295 0H441L295 200.5Z" fill="#3EEB3B"/>
                            <path d="M148 0H0L148 200.5H294L148 0Z" fill="#86FF8B"/>
                            <circle cx="224.5" cy="295.5" r="134.5" fill="#D7961A"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M224 399C281.438 399 328 352.438 328 295C328 237.562 281.438 191 224 191C166.562 191 120 237.562 120 295C120 352.438 166.562 399 224 399ZM224 375C268.183 375 304 339.183 304 295C304 250.817 268.183 215 224 215C179.817 215 144 250.817 144 295C144 339.183 179.817 375 224 375Z" fill="#B7872B"/>
                        </svg>
                    : minifigure.position}
                </p>
            </div>
            {/*
              Progressive Image Loading:
              - While loading: shows placeholder SVG + invisible 0x0 img that triggers the load
              - Once loaded: swaps to show the full image (instant since it's cached)
              - The h-20 on the container ensures consistent row height during loading
            */}
            <div className="grow basis-0 flex justify-center items-center h-20">
                {imageLoaded ? (
                    // Image has loaded - show it at full size
                    <img
                        className="h-20 object-contain"
                        src={`${minifigureImagePath}/${minifigure.minifig_id}.png`}
                        alt={minifigure.minifig_id}
                    />
                ) : (
                    // Image still loading - show placeholder with hidden img to detect load
                    <>
                        <MinifigurePlaceholder />
                        {/* Hidden image that loads in background and triggers onLoad when complete */}
                        <img
                            className="h-0 w-0"
                            src={`${minifigureImagePath}/${minifigure.minifig_id}.png`}
                            alt=""
                            onLoad={() => setImageLoaded(true)}
                        />
                    </>
                )}
            </div>
            <div className="grow-[4] basis-0 flex items-center">
                <p className="">{minifigure.character_name} <span className="hidden md:inline-block">({minifigure.minifig_id})</span></p>
            </div>
            <div className="grow basis-0 flex justify-center items-center">
                <p className="">{Math.round(minifigure.rating)}</p>
            </div>
            <div className="grow basis-0 flex justify-center items-center">
                <NavLink className="text-center bg-green-500 p-2 rounded-md" to={`${url}/${minifigure.theme_url}/minifigures/${minifigure.minifig_id}`}>View Stats</NavLink>
            </div>
        </div>
    );
};

// Component to render the minifigure list once data is loaded
const MinifigureList = ({ minifigures, url, minifigureImagePath, totalRatings }) => {
    return (
        <>
            <div className="flex flex-col gap-2 justify-center py-5">
                <h1 className="text-2xl font-bold text-center">The 100 Highest Rated LEGO Minifigures</h1>
                <p className="text-center">
                    Based on{" "}
                    <span className="font-bold text-green-500">
                        {totalRatings.totalEntries?.toLocaleString()}
                    </span>{" "}
                    user rankings
                </p>
            </div>

            <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-50 p-3">
                <p className="grow basis-0 text-center font-bold hidden md:block">24h</p>
                <p className="grow basis-0 text-center font-bold hidden md:block">Position</p>
                <p className="grow basis-0 text-center font-bold block md:hidden">#</p>
                <p className="grow basis-0 text-center"></p>
                <p className="grow-[4] basis-0 font-bold">Name</p>
                <p className="grow basis-0 text-center font-bold">Rating</p>
                <p className="grow basis-0"></p>
            </div>

            {minifigures.map((minifigure) => (
                <MinifigureRow
                    key={minifigure.id}
                    minifigure={minifigure}
                    url={url}
                    minifigureImagePath={minifigureImagePath}
                />
            ))}
        </>
    );
};

/*
  AllResultsListSkeleton - Full page skeleton including title section.

  Includes the page title and "Based on X user rankings" text with a
  placeholder, plus the same animated rows as MinifigureListSkeleton.
  Used as the Suspense fallback for the main AllResultsPage.
*/
const AllResultsListSkeleton = () => {
    const skeletonRows = Array.from({ length: 20 }, (_, i) => i + 1);

    return (
        <>
            <div className="flex flex-col gap-2 justify-center py-5">
                <h1 className="text-2xl font-bold text-center">The 100 Highest Rated LEGO Minifigures</h1>
                <p className="text-center">
                    Based on{" "}
                    <span className="font-bold text-green-500">...</span>{" "}
                    user rankings
                </p>
            </div>

            <div className="flex gap-2 md:gap-10 border-b border-white border-opacity-50 p-3">
                <p className="grow basis-0 text-center font-bold hidden md:block">24h</p>
                <p className="grow basis-0 text-center font-bold hidden md:block">Position</p>
                <p className="grow basis-0 text-center font-bold block md:hidden">#</p>
                <p className="grow basis-0 text-center"></p>
                <p className="grow-[4] basis-0 font-bold">Name</p>
                <p className="grow basis-0 text-center font-bold">Rating</p>
                <p className="grow basis-0"></p>
            </div>
            {skeletonRows.map((position) => (
                <div key={position} className="flex gap-2 md:gap-10 border-b border-white border-opacity-20 p-3 animate-pulse">
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="w-6 h-6 bg-gray-700 rounded"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <p className="text-gray-500">{position}</p>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <MinifigurePlaceholder />
                    </div>
                    <div className="grow-[4] basis-0 flex items-center">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="h-4 bg-gray-700 rounded w-12"></div>
                    </div>
                    <div className="grow basis-0 flex justify-center items-center">
                        <div className="bg-gray-700 p-2 rounded-md w-20 h-10"></div>
                    </div>
                </div>
            ))}
        </>
    );
};

const AllResultsPage = () => {
    const url = useResolvedPath("").pathname;
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const minifigureImagePath = "/images/minifigures";

    const data = useLoaderData();
    const [totalRatings, setTotalRatings] = useState("");

    useEffect(() => {
        const fetchTotalRatings = async () => {
            try {
                const response = await fetch(`${apiUrl}/getTotalRatings.php`);
                const data = await response.json();
                setTotalRatings(data);
            } catch (error) {
                console.error("Error fetching total ratings", error);
            }
        };
        fetchTotalRatings();
    }, [apiUrl]);

    return (
        <div className="container mx-auto text-sm md:text-md lg:text-lg">
            {/*
              Suspense + Await pattern for deferred data:
              - Suspense: shows skeleton fallback while data.minifigures promise is pending
              - Await: waits for the promise to resolve, then renders children with the data
              - This creates a smooth loading experience where users see immediate feedback
            */}
            <Suspense fallback={<AllResultsListSkeleton />}>
                <Await resolve={data.minifigures}>
                    {(minifigures) => (
                        <MinifigureList
                            minifigures={minifigures}
                            url={url}
                            minifigureImagePath={minifigureImagePath}
                            totalRatings={totalRatings}
                        />
                    )}
                </Await>
            </Suspense>
        </div>
    );
};

export default AllResultsPage;

/*
  Loader function using React Router's defer() for immediate navigation.

  Without defer: The old page stays visible while the API loads,
                 giving no feedback to the user.

  With defer: The page navigates immediately and shows a skeleton loading state
              while the API request completes in the background.

  How it works:
  1. defer() wraps the fetch promise without awaiting it
  2. The component renders immediately with the unresolved promise
  3. <Suspense> shows fallback skeleton while promise is pending
  4. <Await> renders the actual content once promise resolves
*/
export const topMinifiguresLoader = async () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    return defer({
        minifigures: fetch(`${apiUrl}/getTopMinifigures.php`)
            .then(res => res.json())
    });
};
